import React, { useState, useEffect } from 'react';
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";
import { addVendor, getAllVendors, updateVendorStatus, updateVendor } from '../../api/vendorApi';
import { useAuth } from '../../context/AuthContext';
import "../../styles.css";

// Fixed catalog — vendors must be classified into exactly one of these.
export const MATERIAL_CATEGORIES = [
  "Floor Covering",
  "Raw Materials",
  "Hardware and Accessories",
  "Glass & Mirrors",
  "Sliding Solutions",
  "Stone and Solid Surfaces",
  "Lighting",
  "Plumbing & Sanitary",
  "Movable Furniture",
  "Decor and Artifacts",
  "Appliances",
  "Home Automation & Security",
  "Outdoor and Landscaping",
  "Fabric and Upholstery",
  "Miscellaneous",
  "Additional Works",
];

// Only some categories break down further. Categories absent from this map
// (Floor Covering, Appliances, Home Automation & Security, Outdoor and
// Landscaping) have no sub-category — the field stays hidden for them.
export const MATERIAL_SUBCATEGORIES = {
  "Raw Materials": ["Plywood and Laminates", "Laminate", "Pre-Lam Boards", "Edge Banding", "Decor Panels", "CNC Panels", "Ready Made Panels"],
  "Hardware and Accessories": ["Hardwares", "Accessories"],
  "Glass & Mirrors": ["Glass & Mirrors", "Profile Shutters", "Shower Cubicles"],
  "Sliding Solutions": ["Aristo", "Sliding Solutions"],
  "Stone and Solid Surfaces": ["Granite & Quartz", "Tiles", "Stone Cladding", "Wall Murals", "Sand Stones", "FRP Decors"],
  "Lighting": ["Basic Lighting", "Decor Lighting"],
  "Plumbing & Sanitary": ["Plumbing Fittings", "Plumbing Fixtures", "Sanitary Wares"],
  "Movable Furniture": ["Movable Furnitures", "Ready Made Units"],
  "Decor and Artifacts": ["Home Decors"],
  "Fabric and Upholstery": ["Headboard and Cushions", "Fabric"],
  "Miscellaneous": ["Local Purchase", "Transportation, Loading and Unloading", "Debris Removal", "Cleaning", "Other Miscellaneous"],
};

const MaterialVendors = ({ accessLevel }) => {
  const { user } = useAuth();
  const isMainUser = user?.user_type === 'main';
  const isViewOnly = !isMainUser && accessLevel === 'view_only';
  const isMobile = useMediaQuery("(max-width:900px)");
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [addVendorDialogOpen, setAddVendorDialogOpen] = useState(false);
  const [editVendorDialogOpen, setEditVendorDialogOpen] = useState(false);
  const [vendorName, setVendorName] = useState("");
  const [expenseDescription, setExpenseDescription] = useState("");
  const [vendorCategory, setVendorCategory] = useState(null);
  const [vendorSubCategory, setVendorSubCategory] = useState(null);
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vendorNameError, setVendorNameError] = useState("");
  const [expenseDescriptionError, setExpenseDescriptionError] = useState("");
  const [vendorCategoryError, setVendorCategoryError] = useState("");
  const [vendorSubCategoryError, setVendorSubCategoryError] = useState("");
  // Server-side pagination + search — this list can run into the hundreds,
  // so fetching everything and filtering client-side (the old behaviour)
  // meant slow initial loads that only got worse as the vendor list grew.
  const VENDOR_PAGE_SIZE = 30;
  const [vendorPage, setVendorPage] = useState(1);
  const [vendorTotal, setVendorTotal] = useState(0);
  const [vendorLoading, setVendorLoading] = useState(false);

  // Sub-category options for whatever category is currently selected — empty
  // when that category has none, which is also what hides the field.
  const subCategoryOptions = MATERIAL_SUBCATEGORIES[vendorCategory] || [];
  const isSubCategoryApplicable = subCategoryOptions.length > 0;

  // Debounced so search genuinely goes to the server (per the request), but
  // doesn't fire a new request on every single keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);
  useEffect(() => {
    setVendorPage(1);
  }, [tabValue, debouncedSearch]);

  useEffect(() => {
    fetchVendors();
    // eslint-disable-next-line
  }, [tabValue, user, debouncedSearch, vendorPage]);

  // While the add/edit sheet is open on mobile, hide the floating bottom pill
  // so the form is never covered.
  useEffect(() => {
    const open = isMobile && (addVendorDialogOpen || editVendorDialogOpen);
    document.body.classList.toggle("arcdx-sheet-open", open);
    return () => document.body.classList.remove("arcdx-sheet-open");
  }, [isMobile, addVendorDialogOpen, editVendorDialogOpen]);

  const fetchVendors = async () => {
    try {
      if (!user?.company_id) return;
      setVendorLoading(true);

      const params = {
        company_id: user.company_id,
        vendor_type: 'material_vendor',
        vendor_status: tabValue === 0 ? 'active' : 'inactive',
        page: vendorPage,
        limit: VENDOR_PAGE_SIZE,
      };
      if (debouncedSearch) params.search = debouncedSearch;

      const response = await getAllVendors(params);
      if (response.success) {
        setVendors(response.data);
        setVendorTotal(response.total ?? response.data.length);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    } finally {
      setVendorLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleAddVendorClick = () => {
    setAddVendorDialogOpen(true);
  };

  const handleAddVendorClose = () => {
    setAddVendorDialogOpen(false);
    setVendorName("");
    setExpenseDescription("");
    setVendorCategory(null);
    setVendorSubCategory(null);
    setVendorNameError("");
    setExpenseDescriptionError("");
    setVendorCategoryError("");
    setVendorSubCategoryError("");
  };

  const handleAddVendor = async () => {
    // Clear previous errors
    setVendorNameError("");
    setExpenseDescriptionError("");
    setVendorCategoryError("");
    setVendorSubCategoryError("");

    // Validate required fields
    let hasError = false;
    if (!vendorName.trim()) {
      setVendorNameError("Please add the Details");
      hasError = true;
    }
    if (!expenseDescription.trim()) {
      setExpenseDescriptionError("Please add the Details");
      hasError = true;
    }
    if (!vendorCategory) {
      setVendorCategoryError("Please select a Material Category");
      hasError = true;
    }
    if (isSubCategoryApplicable && !vendorSubCategory) {
      setVendorSubCategoryError("Please select a Sub-Category");
      hasError = true;
    }

    if (hasError) return;

    try {
      const vendorData = {
        vendor_name: vendorName,
        expense_description: expenseDescription,
        vendor_type: 'material_vendor',
        vendor_category: vendorCategory,
        vendor_sub_category: isSubCategoryApplicable ? vendorSubCategory : ''
      };

      const response = await addVendor(vendorData);
      if (response.success) {
        handleAddVendorClose();
        fetchVendors();
      }
    } catch (error) {
      console.error("Error adding vendor:", error);
    }
  };

  const handleEditVendorClick = (vendor) => {
    setEditingVendor(vendor);
    setVendorName(vendor.vendor_name);
    setExpenseDescription(vendor.expense_description || "");
    setVendorCategory(vendor.vendor_category || null);
    setVendorSubCategory(vendor.vendor_sub_category || null);
    setEditVendorDialogOpen(true);
  };

  const handleEditVendorClose = () => {
    setEditVendorDialogOpen(false);
    setEditingVendor(null);
    setVendorName("");
    setExpenseDescription("");
    setVendorCategory(null);
    setVendorSubCategory(null);
    setVendorNameError("");
    setExpenseDescriptionError("");
    setVendorCategoryError("");
    setVendorSubCategoryError("");
  };

  const handleUpdateVendor = async () => {
    // Clear previous errors
    setVendorNameError("");
    setExpenseDescriptionError("");
    setVendorCategoryError("");
    setVendorSubCategoryError("");

    // Validate required fields
    let hasError = false;
    if (!vendorName.trim()) {
      setVendorNameError("Please add the Details");
      hasError = true;
    }
    if (!expenseDescription.trim()) {
      setExpenseDescriptionError("Please add the Details");
      hasError = true;
    }
    if (!vendorCategory) {
      setVendorCategoryError("Please select a Material Category");
      hasError = true;
    }
    if (isSubCategoryApplicable && !vendorSubCategory) {
      setVendorSubCategoryError("Please select a Sub-Category");
      hasError = true;
    }

    if (hasError) return;

    try {
      const response = await updateVendor(editingVendor._id, {
        vendor_name: vendorName,
        expense_description: expenseDescription,
        vendor_category: vendorCategory,
        vendor_sub_category: isSubCategoryApplicable ? vendorSubCategory : ''
      });
      if (response.success) {
        handleEditVendorClose();
        fetchVendors();
      }
    } catch (error) {
      console.error("Error updating vendor:", error);
    }
  };

  const handleStatusChange = async (vendorId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const response = await updateVendorStatus(vendorId, newStatus);
      if (response.success) {
        fetchVendors();
      }
    } catch (error) {
      console.error("Error updating vendor status:", error);
    }
  };

  // vendors is already server-filtered (search) and server-paginated (page/limit).
  const filteredVendors = vendors;
  const vendorTotalPages = Math.max(1, Math.ceil(vendorTotal / VENDOR_PAGE_SIZE));

  return (
    <Box>
      {/* ===== MOBILE: vendor directory ===== */}
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <div className="arcdx-v-chiprow">
          {["Active", "In-Active"].map((label, i) => (
            <div key={label} className={`arcdx-v-chip${tabValue === i ? " active" : ""}`} onClick={() => setTabValue(i)}>
              <span className="dot" style={{ background: i === 0 ? "#59AC77" : "#98a2b3" }} />
              {label}
            </div>
          ))}
        </div>
        <div className="arcdx-v-search" style={{ marginTop: 8, marginBottom: 12 }}>
          <SearchIcon sx={{ fontSize: 20, color: "#98a2b3" }} />
          <input placeholder="Search vendors…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        {filteredVendors.length === 0 ? (
          <div className="arcdx-v-empty">
            <div className="big">🏪</div>
            No {tabValue === 0 ? "active" : "inactive"} material vendors
          </div>
        ) : (
          filteredVendors.map((vendor) => (
            <div key={vendor._id} className="arcdx-v-card" onClick={() => !isViewOnly && handleEditVendorClick(vendor)}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Box sx={{ width: 42, height: 42, borderRadius: "13px", background: "linear-gradient(135deg, #e3f2ff, #cfe9ff)", color: "#1f6fd6", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "16px", flexShrink: 0 }}>
                  {(vendor.vendor_name || "?").charAt(0).toUpperCase()}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ fontSize: "14.5px", fontWeight: 700, color: "#101828", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {vendor.vendor_name}
                  </Box>
                  <Box sx={{ fontSize: "12px", color: "#667085", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {vendor.expense_description || "—"}
                  </Box>
                  {vendor.vendor_category && (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px", marginTop: "4px" }}>
                      <span className="arcdx-v-status" style={{ background: "#e3f2ff", color: "#1565C0", padding: "3px 9px" }}>
                        {vendor.vendor_category}
                      </span>
                      {vendor.vendor_sub_category && (
                        <Box sx={{ display: "flex", alignItems: "center", marginLeft: "6px" }}>
                          <Box sx={{ width: "10px", height: "10px", borderLeft: "1.5px solid #d0d5dd", borderBottom: "1.5px solid #d0d5dd", borderBottomLeftRadius: "6px", marginRight: "5px", alignSelf: "flex-start", marginTop: "-6px" }} />
                          <span className="arcdx-v-status" style={{ background: "#f4ebff", color: "#6941C6", padding: "3px 9px" }}>
                            {vendor.vendor_sub_category}
                          </span>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
                <Box onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={vendor.vendor_status === 'active'}
                    onChange={() => handleStatusChange(vendor._id, vendor.vendor_status)}
                    disabled={isViewOnly}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#39aafc' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#39aafc' },
                    }}
                  />
                </Box>
              </Box>
            </div>
          ))
        )}
        {vendorTotal > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, mt: 1.5, mb: 1 }}>
            <Button
              onClick={() => setVendorPage((p) => Math.max(1, p - 1))}
              disabled={vendorPage <= 1}
              sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#344054", background: "#f2f4f7", borderRadius: "9px", px: 1.5, py: "6px", minWidth: 0, "&.Mui-disabled": { color: "#c7ccd4", background: "#f7f8fa" } }}
            >
              Previous
            </Button>
            <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#344054" }}>
              Page {vendorPage} of {vendorTotalPages}
            </Typography>
            <Button
              onClick={() => setVendorPage((p) => Math.min(vendorTotalPages, p + 1))}
              disabled={vendorPage >= vendorTotalPages}
              sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#344054", background: "#f2f4f7", borderRadius: "9px", px: 1.5, py: "6px", minWidth: 0, "&.Mui-disabled": { color: "#c7ccd4", background: "#f7f8fa" } }}
            >
              Next
            </Button>
          </Box>
        )}
        {/* Floating Add Vendor — emerald supplier truck */}
        {!isViewOnly && (
          <button type="button" className="arcdx-v-fab material" onClick={handleAddVendorClick} aria-label="Add material vendor">
            <LocalShippingRoundedIcon sx={{ fontSize: 26 }} />
          </button>
        )}
      </Box>

      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          marginBottom: "20px",
          gap: 2
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            gap: "4px",
            background: "#f2f4f7",
            borderRadius: "13px",
            p: "4px",
          }}
        >
          {["Active", "In-Active"].map((label, idx) => {
            const active = tabValue === idx;
            return (
              <Box
                key={label}
                onClick={() => handleTabChange(null, idx)}
                sx={{
                  px: 1.75,
                  py: "8px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: active ? 700 : 600,
                  fontFamily: "Roboto, Arial, sans-serif",
                  color: active ? "#1565C0" : "#667085",
                  background: active ? "#ffffff" : "transparent",
                  boxShadow: active ? "0 1px 3px rgba(16,24,40,0.10)" : "none",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  "&:hover": { color: "#1565C0" },
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>

        {!isViewOnly && (
        <Button
          variant="contained"
          onClick={handleAddVendorClick}
          sx={{
            marginLeft: "2vw",
            background: "linear-gradient(135deg, #4db2fc, #1565C0)",
            color: "white",
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 700,
            minWidth: "120px",
            height: "40px",
            borderRadius: "13px",
            boxShadow: "0 8px 20px rgba(21, 101, 192, 0.28)",
            "&:hover": {
              background: "linear-gradient(135deg, #39aafc, #0d47a1)",
              boxShadow: "0 10px 24px rgba(21, 101, 192, 0.38)",
            },
          }}
        >
          Add Vendors
        </Button>
        )}

        <Box className="client-search-bar">
          <SearchIcon className="search-icon-wrapper" />
          <InputBase
            className="styled-input-base"
            placeholder="Search vendors…"
            inputProps={{ "aria-label": "search" }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Box>
      </Box>

      {/* Tab Content */}
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <TableContainer component={Paper} sx={{ boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)" }}>
          <Table sx={{ minWidth: 650 }} aria-label="vendors table">
            <TableHead>
              <TableRow>
                <TableCell className="table-head-cell" sx={{ width: "30px", textAlign: "center" }}>
                  S/No
                </TableCell>
                <TableCell className="table-head-cell" sx={{ width: "140px" }}>
                  Vendor Name
                </TableCell>
                <TableCell className="table-head-cell" sx={{ width: "150px" }}>
                  Material Category
                </TableCell>
                <TableCell className="table-head-cell" sx={{ width: "140px" }}>
                  Expense Description
                </TableCell>
                <TableCell className="table-head-cell" sx={{ width: "120px", textAlign: "center" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vendors.map((vendor, index) => (
                <TableRow key={vendor._id}>
                  <TableCell className="table-body-cell" sx={{ textAlign: "center" }}>
                    {(vendorPage - 1) * VENDOR_PAGE_SIZE + index + 1}
                  </TableCell>
                  <TableCell className="table-body-cell">
                    {vendor.vendor_name}
                  </TableCell>
                  <TableCell className="table-body-cell">
                    {vendor.vendor_category ? (
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
                        <span className="arcdx-v-status" style={{ background: "#e3f2ff", color: "#1565C0" }}>
                          {vendor.vendor_category}
                        </span>
                        {vendor.vendor_sub_category && (
                          <Box sx={{ display: "flex", alignItems: "center", marginLeft: "6px" }}>
                            <Box sx={{ width: "10px", height: "10px", borderLeft: "1.5px solid #d0d5dd", borderBottom: "1.5px solid #d0d5dd", borderBottomLeftRadius: "6px", marginRight: "5px", alignSelf: "flex-start", marginTop: "-6px" }} />
                            <span className="arcdx-v-status" style={{ background: "#f4ebff", color: "#6941C6" }}>
                              {vendor.vendor_sub_category}
                            </span>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <span style={{ color: "#98a2b3", fontSize: "12px" }}>—</span>
                    )}
                  </TableCell>
                  <TableCell className="table-body-cell">
                    {vendor.expense_description || ''}
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "center" }}>
                    {!isViewOnly && (
                      <IconButton
                        onClick={() => handleEditVendorClick(vendor)}
                        sx={{ padding: "4px", marginRight: "8px" }}
                      >
                        <EditIcon sx={{ fontSize: "18px", color: "#1E40AF" }} />
                      </IconButton>
                    )}
                    <Tooltip title={vendor.vendor_status === 'active' ? "Make Vendor In-Active" : "Make Vendor Active"}>
                      <Switch
                        checked={vendor.vendor_status === 'active'}
                        onChange={() => handleStatusChange(vendor._id, vendor.vendor_status)}
                        disabled={isViewOnly}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#39aafc',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#39aafc',
                          },
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {vendorTotal > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, padding: "14px 4px" }}>
            <Typography sx={{ fontSize: "12.5px", color: "#98a2b3", fontWeight: 600 }}>
              Showing {(vendorPage - 1) * VENDOR_PAGE_SIZE + 1}–{Math.min(vendorPage * VENDOR_PAGE_SIZE, vendorTotal)} of {vendorTotal}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                onClick={() => setVendorPage((p) => Math.max(1, p - 1))}
                disabled={vendorPage <= 1}
                sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#344054", background: "#f2f4f7", borderRadius: "9px", px: 1.5, py: "6px", minWidth: 0, "&.Mui-disabled": { color: "#c7ccd4", background: "#f7f8fa" } }}
              >
                Previous
              </Button>
              <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#344054", px: 1 }}>
                Page {vendorPage} of {vendorTotalPages}
              </Typography>
              <Button
                onClick={() => setVendorPage((p) => Math.min(vendorTotalPages, p + 1))}
                disabled={vendorPage >= vendorTotalPages}
                sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#344054", background: "#f2f4f7", borderRadius: "9px", px: 1.5, py: "6px", minWidth: 0, "&.Mui-disabled": { color: "#c7ccd4", background: "#f7f8fa" } }}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Add Vendor Dialog (desktop; mobile uses the bottom sheet) */}
      <Dialog
        open={!isMobile && addVendorDialogOpen}
        onClose={handleAddVendorClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(155deg, #4db2fc 0%, #1565C0 100%)", pt: 3, pb: 2.5, px: 3, color: "#fff", textAlign: "center" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 800, fontFamily: "Roboto, sans-serif" }}>Add Material Vendor</Typography>
        </Box>
        <DialogContent sx={{ paddingTop: "20px !important", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <TextField
            label="Vendor Name"
            variant="outlined"
            fullWidth
            value={vendorName}
            onChange={(e) => {
              setVendorName(e.target.value);
              if (e.target.value.trim()) setVendorNameError("");
            }}
            error={!!vendorNameError}
            helperText={vendorNameError}
            InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
            sx={{
              marginBottom: "20px",
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiFormHelperText-root": {
                fontSize: "12px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
            }}
          />

          <Autocomplete
            options={MATERIAL_CATEGORIES}
            value={vendorCategory}
            fullWidth
            onChange={(e, newValue) => {
              setVendorCategory(newValue);
              if (newValue) setVendorCategoryError("");
              setVendorSubCategory(null);
              setVendorSubCategoryError("");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Material Category"
                required
                error={!!vendorCategoryError}
                helperText={vendorCategoryError}
                InputProps={{ ...params.InputProps, sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
              />
            )}
            sx={{
              marginBottom: "20px",
              width: "100%",
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiFormHelperText-root": {
                fontSize: "12px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
            }}
          />

          {isSubCategoryApplicable && (
            <Autocomplete
              options={subCategoryOptions}
              value={vendorSubCategory}
              fullWidth
              onChange={(e, newValue) => {
                setVendorSubCategory(newValue);
                if (newValue) setVendorSubCategoryError("");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sub-Category"
                  required
                  error={!!vendorSubCategoryError}
                  helperText={vendorSubCategoryError}
                  InputProps={{ ...params.InputProps, sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
                />
              )}
              sx={{
                marginBottom: "20px",
                width: "100%",
                "& .MuiInputLabel-root": {
                  fontSize: "13px",
                  fontFamily: "Roboto, Arial, sans-serif",
                },
                "& .MuiOutlinedInput-root": {
                  fontSize: "13px",
                  fontFamily: "Roboto, Arial, sans-serif",
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "12px",
                  fontFamily: "Roboto, Arial, sans-serif",
                },
              }}
            />
          )}

          <TextField
            label="Expense Description"
            variant="outlined"
            fullWidth
            required
            value={expenseDescription}
            onChange={(e) => {
              setExpenseDescription(e.target.value);
              if (e.target.value.trim()) setExpenseDescriptionError("");
            }}
            error={!!expenseDescriptionError}
            helperText={expenseDescriptionError}
            inputProps={{
              style: { textTransform: 'capitalize' }
            }}
            InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
            sx={{
              marginBottom: "20px",
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiFormHelperText-root": {
                fontSize: "12px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
            }}
          />

          <Button
            onClick={handleAddVendor}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #4db2fc, #1565C0)",
              color: "white",
              fontSize: "13px",
              fontFamily: "Roboto, Arial, sans-serif",
              textTransform: "none",
              fontWeight: 800,
              minWidth: "120px",
              height: "44px",
              borderRadius: "14px",
              boxShadow: "0 8px 20px rgba(21,101,192,0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #39aafc, #0d47a1)",
                boxShadow: "0 10px 24px rgba(21, 101, 192, 0.38)",
              },
            }}
          >
            Add
          </Button>
        </DialogContent>
      </Dialog>

      {/* Edit Vendor Dialog (desktop; mobile uses the bottom sheet) */}
      <Dialog
        open={!isMobile && editVendorDialogOpen}
        onClose={handleEditVendorClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(155deg, #4db2fc 0%, #1565C0 100%)", pt: 3, pb: 2.5, px: 3, color: "#fff", textAlign: "center" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 800, fontFamily: "Roboto, sans-serif" }}>Edit Material Vendor</Typography>
        </Box>
        <DialogContent sx={{ paddingTop: "20px !important", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <TextField
            label="Vendor Name"
            variant="outlined"
            fullWidth
            value={vendorName}
            onChange={(e) => {
              setVendorName(e.target.value);
              if (e.target.value.trim()) setVendorNameError("");
            }}
            error={!!vendorNameError}
            helperText={vendorNameError}
            InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
            sx={{
              marginBottom: "20px",
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiFormHelperText-root": {
                fontSize: "12px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
            }}
          />

          <Autocomplete
            options={MATERIAL_CATEGORIES}
            value={vendorCategory}
            fullWidth
            onChange={(e, newValue) => {
              setVendorCategory(newValue);
              if (newValue) setVendorCategoryError("");
              setVendorSubCategory(null);
              setVendorSubCategoryError("");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Material Category"
                required
                error={!!vendorCategoryError}
                helperText={vendorCategoryError}
                InputProps={{ ...params.InputProps, sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
              />
            )}
            sx={{
              marginBottom: "20px",
              width: "100%",
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiFormHelperText-root": {
                fontSize: "12px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
            }}
          />

          {isSubCategoryApplicable && (
            <Autocomplete
              options={subCategoryOptions}
              value={vendorSubCategory}
              fullWidth
              onChange={(e, newValue) => {
                setVendorSubCategory(newValue);
                if (newValue) setVendorSubCategoryError("");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sub-Category"
                  required
                  error={!!vendorSubCategoryError}
                  helperText={vendorSubCategoryError}
                  InputProps={{ ...params.InputProps, sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
                />
              )}
              sx={{
                marginBottom: "20px",
                width: "100%",
                "& .MuiInputLabel-root": {
                  fontSize: "13px",
                  fontFamily: "Roboto, Arial, sans-serif",
                },
                "& .MuiOutlinedInput-root": {
                  fontSize: "13px",
                  fontFamily: "Roboto, Arial, sans-serif",
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "12px",
                  fontFamily: "Roboto, Arial, sans-serif",
                },
              }}
            />
          )}

          <TextField
            label="Expense Description"
            variant="outlined"
            fullWidth
            required
            value={expenseDescription}
            onChange={(e) => {
              setExpenseDescription(e.target.value);
              if (e.target.value.trim()) setExpenseDescriptionError("");
            }}
            error={!!expenseDescriptionError}
            helperText={expenseDescriptionError}
            inputProps={{
              style: { textTransform: 'capitalize' }
            }}
            InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
            sx={{
              marginBottom: "20px",
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
              "& .MuiFormHelperText-root": {
                fontSize: "12px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
            }}
          />

          <Button
            onClick={handleUpdateVendor}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #4db2fc, #1565C0)",
              color: "white",
              fontSize: "13px",
              fontFamily: "Roboto, Arial, sans-serif",
              textTransform: "none",
              fontWeight: 800,
              minWidth: "120px",
              height: "44px",
              borderRadius: "14px",
              boxShadow: "0 8px 20px rgba(21,101,192,0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #39aafc, #0d47a1)",
                boxShadow: "0 10px 24px rgba(21, 101, 192, 0.38)",
              },
            }}
          >
            Update
          </Button>
        </DialogContent>
      </Dialog>

      {/* ===== MOBILE SHEET: Add / Edit Material Vendor ===== */}
      {isMobile && (addVendorDialogOpen || editVendorDialogOpen) && (() => {
        const editing = editVendorDialogOpen;
        const close = editing ? handleEditVendorClose : handleAddVendorClose;
        const save = editing ? handleUpdateVendor : handleAddVendor;
        const vIn = { "& .MuiOutlinedInput-root": { borderRadius: "14px", backgroundColor: "#f9fafb", fontSize: "15px", "& fieldset": { borderColor: "#e4e7ec" }, "&.Mui-focused fieldset": { borderColor: "#39aafc", borderWidth: "1.5px" } }, "& .MuiInputBase-input": { fontSize: "15px", padding: "13px 14px" }, "& .MuiInputLabel-root": { fontSize: "13px" } };
        return (
          <>
            <div className="arcdx-sheet-back" onClick={close} />
            <div className="arcdx-sheet">
              <div className="arcdx-sheet-grip" />
              <div className="arcdx-v-sheet-head">
                <span className="arcdx-v-sheet-title">{editing ? "Edit Vendor" : "New Material Vendor"}</span>
                <IconButton onClick={close} size="small" sx={{ background: "#f2f4f7" }}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </div>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <TextField
                  label="Vendor Name"
                  value={vendorName}
                  onChange={(e) => { setVendorName(e.target.value); if (e.target.value.trim()) setVendorNameError(""); }}
                  error={!!vendorNameError}
                  helperText={vendorNameError}
                  sx={vIn}
                  fullWidth
                />
                <Autocomplete
                  options={MATERIAL_CATEGORIES}
                  value={vendorCategory}
                  fullWidth
                  onChange={(e, newValue) => { setVendorCategory(newValue); if (newValue) setVendorCategoryError(""); setVendorSubCategory(null); setVendorSubCategoryError(""); }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Material Category"
                      required
                      error={!!vendorCategoryError}
                      helperText={vendorCategoryError}
                    />
                  )}
                  sx={vIn}
                />
                {isSubCategoryApplicable && (
                  <Autocomplete
                    options={subCategoryOptions}
                    value={vendorSubCategory}
                    fullWidth
                    onChange={(e, newValue) => { setVendorSubCategory(newValue); if (newValue) setVendorSubCategoryError(""); }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sub-Category"
                        required
                        error={!!vendorSubCategoryError}
                        helperText={vendorSubCategoryError}
                      />
                    )}
                    sx={vIn}
                  />
                )}
                <TextField
                  label="Expense Description"
                  value={expenseDescription}
                  onChange={(e) => { setExpenseDescription(e.target.value); if (e.target.value.trim()) setExpenseDescriptionError(""); }}
                  error={!!expenseDescriptionError}
                  helperText={expenseDescriptionError}
                  inputProps={{ style: { textTransform: "capitalize" } }}
                  sx={vIn}
                  fullWidth
                />
                <Button
                  onClick={save}
                  sx={{ py: 1.5, borderRadius: "14px", background: "linear-gradient(135deg,#39aafc,#1f8fe8)", color: "#fff", fontWeight: 700, textTransform: "none", fontSize: "16px", boxShadow: "0 8px 20px rgba(31,143,232,0.4)" }}
                  fullWidth
                >
                  {editing ? "Save Changes" : "Add Vendor"}
                </Button>
              </Box>
            </div>
          </>
        );
      })()}
    </Box>
  );
};

export default MaterialVendors;
